import { makeStyles, Text, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { WebScreenProps } from 'home/stack/types'
import React, { useLayoutEffect } from 'react'
import { Alert, Platform, View } from 'react-native'
import { URL as RNURL } from 'react-native-url-polyfill'
import { WebView, WebViewMessageEvent } from 'react-native-webview'
import { showHeaderButton } from 'shared/header/header_button'
import { showHeaderTitle } from 'shared/header/header_title'
import { useUpdateUserRestoreId } from 'users/queries/update_restore_id'

const ALLOWED_HOSTS = [
  'nextforce.us',
  'nextmanager.us',
  'freshchat.com',
  'freshdesk.com',
]

function isUrlAllowed(url: string): boolean {
  try {
    const normalizedUrl = url.trim()
    const parsedUrl =
      Platform.OS === 'web' ? new URL(normalizedUrl) : new RNURL(normalizedUrl)
    if (parsedUrl.protocol !== 'https:') return false

    return ALLOWED_HOSTS.some(
      host =>
        parsedUrl.hostname === host || parsedUrl.hostname.endsWith(`.${host}`)
    )
  } catch {
    return false
  }
}

export function WebScreen({ route, navigation }: WebScreenProps) {
  const { theme } = useTheme()
  const updateUserRestoreIdMutation = useUpdateUserRestoreId()

  useLayoutEffect(() => {
    showHeaderTitle(navigation, route.params.title)

    showHeaderButton({
      navigation,
      theme,
      title: !route.params.hideDoneButton ? t('done') : undefined,
      onPress: async () => {
        navigation.goBack()
      },
    })
  }, [navigation, route.params.hideDoneButton, route.params.title, theme])

  const onMessage = async (event: WebViewMessageEvent) => {
    if (event.nativeEvent.data.startsWith('restoreId')) {
      try {
        await updateUserRestoreIdMutation.mutateAsync({
          restoreId: event.nativeEvent.data.split(':')[1],
        })
      } catch (error) {
        console.error(error)
      }
    }
    if (event.nativeEvent.data === 'CLOSE') {
      navigation.goBack()
    }
  }

  const runFirst = `
      const meta = document.createElement('meta');
      meta.setAttribute('content', 'width=device-width, initial-scale=1');
      meta.setAttribute('name', 'viewport');
      document.getElementsByTagName('head')[0].appendChild(meta);
      true; // note: this is required, or you'll sometimes get silent failures
  `

  const styles = useStyles()

  if (!isUrlAllowed(route.params.url)) {
    return (
      <View style={styles.container}>
        <Text style={{ padding: 20, textAlign: 'center' }}>
          {t('invalidUrl')}
        </Text>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <WebView
        source={{
          uri: route.params.url,
        }}
        renderLoading={LoadingIndicatorView}
        startInLoadingState={true}
        scalesPageToFit={false}
        onMessage={onMessage}
        injectedJavaScriptBeforeContentLoaded={runFirst}
        onShouldStartLoadWithRequest={request => {
          const allowed = isUrlAllowed(request.url)
          if (!allowed) Alert.alert(t('invalidUrl'))
          return allowed
        }}
      />
    </View>
  )
}

function LoadingIndicatorView() {
  return (
    <View style={{ alignItems: 'center', padding: 10 }}>
      <Text>Loading...</Text>
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
  },
}))
